import { html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import type { DatePickerPresentation } from '../../core/protocol/types';
import { isCalendarDate } from '../../core/protocol/validators';
import { toolStyles } from '../styles';
import { ToolElement } from './base';
import { leftIcon, rightIcon } from '../icons';
const toIso = (d: Date) => `${String(d.getFullYear()).padStart(4, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const today = () => toIso(new Date());
const parseDate = (iso: string) => { const d = new Date(0); d.setHours(0, 0, 0, 0); d.setFullYear(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10))); return d; };
const monthOf = (iso: string) => iso.slice(0, 7);
export class OsviDatePicker extends ToolElement {
  static styles = toolStyles;
  protected readonly toolType = 'ui_date_picker';
  @property() variant = 'grid';
  @state() private month = monthOf(today());
  @state() private start = '';
  @state() private end = '';
  private earliest(p: DatePickerPresentation) { return p.min_date && p.min_date > today() ? p.min_date : today(); }
  protected willUpdate(changed: Map<PropertyKey, unknown>) {
    if (changed.has('interaction')) {
      this.start = ''; this.end = '';
      if (this.valid) this.month = monthOf(this.earliest(this.interaction!.presentation as DatePickerPresentation));
    }
  }
  render() {
    const i = this.interaction;
    if (!i) return nothing;
    if (!this.valid) return html`<p>${i.content}</p>`;
    const p = i.presentation as DatePickerPresentation;
    const response = i.response_payload;
    const chosen = response?.kind === 'date_range' ? `${response.value.start_date} – ${response.value.end_date}` : response?.kind === 'date' || response?.kind === 'text' ? response.value : i.selected_option_id;
    if (!this.live) return html`<p class="label">${p.label}</p>${p.description ? html`<p class="description">${p.description}</p>` : nothing}${chosen ? html`<p class="answer">${chosen}</p>` : nothing}`;
    const first = parseDate(`${this.month}-01`);
    const last = new Date(first); last.setMonth(last.getMonth() + 1, 0);
    const earliest = this.earliest(p);
    const days = Array.from({ length: last.getDate() }, (_, n) => `${this.month}-${String(n + 1).padStart(2, '0')}`);
    const hint = p.selection_mode === 'range' ? !this.start ? 'Choose check-in, then check-out.' : !this.end ? `Check-in: ${this.start}. Choose check-out.` : `${this.start} – ${this.end}` : this.start;
    const now = today();
    return html`<p class="label">${p.label}</p>${p.description ? html`<p class="description">${p.description}</p>` : nothing}
      <div class="calendar"><div class="navigation"><button type="button" class="round" aria-label="Previous month" ?disabled=${this.month <= monthOf(earliest)} @click=${() => this.moveMonth(-1, p)}>${leftIcon}</button>
        <span aria-live="polite">${first.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</span>
        <button type="button" class="round" aria-label="Next month" ?disabled=${!!p.max_date && this.month >= monthOf(p.max_date)} @click=${() => this.moveMonth(1, p)}>${rightIcon}</button></div>
      <div class="weekdays" aria-hidden="true">${['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => html`<span>${day}</span>`)}</div>
      <div class="days" role="group" aria-label="Choose date" @keydown=${this.onKey}>
        ${Array.from({ length: first.getDay() }, () => html`<span></span>`)}
        ${days.map((day, n) => html`<button type="button" class=${[day === now ? 'today' : '', day === this.start ? 'start' : '', day === this.end ? 'end' : '', this.start && this.end && day > this.start && day < this.end ? 'in' : ''].filter(Boolean).join(' ')} aria-label=${day} aria-pressed=${day === this.start || day === this.end} ?disabled=${day < earliest || !!p.max_date && day > p.max_date} @click=${() => this.pick(day, p)}>${n + 1}</button>`)}
      </div><p class="hint" aria-live="polite">${hint}</p>
      <button type="button" class="primary" ?disabled=${!this.start || p.selection_mode === 'range' && (!this.end || this.start >= this.end)} @click=${() => this.confirm(p)}>${p.confirm_label ?? 'Confirm date'}</button></div>`;
  }
  private moveMonth(delta: number, p: DatePickerPresentation) {
    const date = parseDate(`${this.month}-01`); date.setMonth(date.getMonth() + delta);
    const next = monthOf(toIso(date));
    if (next >= monthOf(this.earliest(p)) && (!p.max_date || next <= monthOf(p.max_date))) this.month = next;
  }
  private pick(day: string, p: DatePickerPresentation) {
    if (!this.live || !this.valid || !isCalendarDate(day) || day < this.earliest(p) || p.max_date && day > p.max_date) return;
    if (p.selection_mode === 'single' || !this.start || this.end || day <= this.start) { this.start = day; this.end = ''; }
    else this.end = day;
  }
  private confirm(p: DatePickerPresentation) {
    if (!isCalendarDate(this.start) || this.start < this.earliest(p) || p.max_date && this.start > p.max_date) return;
    if (p.selection_mode === 'single') this.respond({ kind: 'date', value: this.start });
    else if (isCalendarDate(this.end) && this.start < this.end && (!p.max_date || this.end <= p.max_date)) this.respond({ kind: 'date_range', value: { start_date: this.start, end_date: this.end } });
  }
  private onKey(e: KeyboardEvent) {
    const steps: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    const step = steps[e.key]; if (step === undefined) return;
    const buttons = [...this.renderRoot.querySelectorAll<HTMLButtonElement>('.days button')];
    const at = buttons.indexOf((this.renderRoot as ShadowRoot).activeElement as HTMLButtonElement);
    if (at < 0) return;
    let next = at + step;
    while (next >= 0 && next < buttons.length && buttons[next]?.disabled) next += step > 0 ? 1 : -1;
    buttons[next]?.focus(); e.preventDefault();
  }
}
